import { makeAutoObservable } from "mobx";
import { IdMap, RArray, RSet } from "../../collections";
import { Price } from "../../pricing/Price";
import { Availability } from "../Availability";
import { OrderLine } from "../OrderLine";
import { Quantity } from "../Quantity";
import { OrderLinePrice } from "../../pricing/OrderLinePrice";
/*
 * Collection of OrderLines added by user
 * It's price is a nominal order price (without delivery and promotions)
 * It's availability depends on availability of all ordered product instances
 */
export class PurchaseOrderPayload {
    constructor(params) {
        this.menu = params.menu;
        this._orderLines = params.orderLines;
        makeAutoObservable(this);
    }
    static empty(menu) {
        return new PurchaseOrderPayload({ menu, orderLines: [] });
    }
    static fromStorageData({ menu, storageData, }) {
        const orderLines = new RArray(storageData).mapOptional((orderLineStorageData) => OrderLine.fromStorageData({ menu, storageData: orderLineStorageData }));
        return new PurchaseOrderPayload({
            menu,
            orderLines: orderLines.raw,
        });
    }
    mapProductInstances(mapping) {
        return new PurchaseOrderPayload({
            menu: this.menu,
            orderLines: this._orderLines.map((orderLine) => orderLine.mapProductInstance(mapping)),
        });
    }
    get orderLines() {
        return new RArray(this._orderLines).filtered((orderLine) => !orderLine.isEmpty);
    }
    get numberOfItems() {
        return this.orderLines.reduce((acc, { quantity }) => acc.add(quantity), Quantity.Zero);
    }
    get isEmpty() {
        return this.numberOfItems.isZero;
    }
    get price() {
        return Price.total(this.orderLines.map((orderline) => OrderLinePrice.totalPrice(orderline)));
    }
    get availability() {
        if (this.isEmpty) {
            return Availability.unavailable("EmptyOrder");
        }
        const orderLinesAvailabilities = this.orderLines.map((orderLine) => orderLine.availability).raw;
        return Availability.dependent("OrderLine", "", Availability.composite(orderLinesAvailabilities));
    }
    addOrderLine(orderLine) {
        if (orderLine.isEmpty) {
            throw new Error(`Unable to add an empty OrderLine to PurchaseOrder`);
        }
        if (!this.menu.includes(orderLine.productTypeIds)) {
            throw new Error(`Unable to add an OrderLine, because some of the product types are offered by restaurant`);
        }
        const existingOrderLineIndex = this._orderLines.findIndex((existingOrderLine) => existingOrderLine.productInstance.eq(orderLine.productInstance));
        if (existingOrderLineIndex === -1) {
            this._orderLines.push(orderLine);
            return;
        }
        const existingOrderLine = this._orderLines[existingOrderLineIndex];
        if (existingOrderLine.isEmpty) {
            this._orderLines[existingOrderLineIndex] = orderLine;
            return;
        }
        existingOrderLine.addQuantity(orderLine.quantity);
    }
    cartCount(productInstance) {
        return this._orderLines
            .filter((orderLine) => productInstance.isCartEquivalentTo(orderLine.productInstance))
            .map((orderLine) => orderLine.quantity)
            .reduce((prev, curr) => prev.add(curr), Quantity.Zero);
    }
    clear() {
        this._orderLines = [];
    }
    get productTypeIds() {
        return this.orderLines
            .map((orderLine) => orderLine.productTypeIds)
            .reduce((acc, ids) => acc.union(ids), RSet.empty());
    }
    get packingItems() {
        return this.orderLines
            .map((orderLine) => orderLine.packingItems)
            .reduce((prev, curr) => prev.unionWith(curr, (a, b) => a.merge(b)), IdMap.empty());
    }
    get checkoutData() {
        return this.orderLines.map((orderLine) => orderLine.checkoutData).raw;
    }
    get storageData() {
        return this.orderLines.map((orderLine) => orderLine.storageData).raw;
    }
}
